"use client";

import LoginForm from "@/components/login/LoginForm";
import Image from "next/image";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import ResetPasswordForm from "../../components/login/ResetPasswordForm";
import CheckEmail from "../../components/login/CheckEmail";
import SetNewPasswordForm from "../../components/login/SetNewPasswordForm";
import PublicRoute from "@/components/auth/PublicRoute";
import { Sparkles, TrendingUp, Shield, Brain, Zap } from "lucide-react";
import { verifyResetToken } from "@/services/auth";
import { cn } from "@/lib/utils";

const LoginPage = () => {
  const searchParams = useSearchParams();
  const [screen, setScreen] = useState<
    "login" | "reset" | "checkEmail" | "setPassword"
  >("login");
  const [resetEmail, setResetEmail] = useState<string>("");

  // Check for reset token in URL
  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      // Verify token and show set password screen
      verifyResetToken(token)
        .then(() => {
          setScreen("setPassword");
        })
        .catch((error) => {
          console.error("Token verification failed:", error);
          // Stay on login screen, token will be handled by SetNewPasswordForm
        });
    }
  }, [searchParams]);

  return (
    <PublicRoute>
      <div className="grid min-h-screen lg:grid-cols-2 relative overflow-hidden">
        {/* Left Panel - Promotional Content */}
        <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 relative overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 -left-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-20 -right-20 w-96 h-96 bg-primary/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0.75s' }} />
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col h-full justify-center space-y-8 animate-in fade-in-0 slide-in-from-left-8 duration-1000">
            {/* Logo */}
            <div className="mb-8">
              <Image
                src="/images/GrowFinX Logo Horizontal.svg"
                alt="GrowFinX Logo"
                width={200}
                height={90}
                unoptimized 
                className="max-w-[200px] h-auto"  
                priority
              />
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-5xl font-bold text-foreground leading-tight">
                Fast, Efficient and
                <br />
                <span className="text-primary">Productive</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-md">
                Transform your banking operations with AI-powered CRM solutions. 
                Streamline workflows, enhance customer relationships, and drive growth 
                with intelligent automation.
              </p>
            </div>

            {/* Feature Icons */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-card/30 backdrop-blur-sm border border-border/30 hover:bg-card/50 transition-all duration-300 group">
                <div className="p-2.5 rounded-lg bg-primary/20 group-hover:bg-primary/30 transition-colors">
                  <Brain className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">AI-Powered</h3>
                  <p className="text-xs text-muted-foreground">Smart Analytics</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 rounded-xl bg-card/30 backdrop-blur-sm border border-border/30 hover:bg-card/50 transition-all duration-300 group">
                <div className="p-2.5 rounded-lg bg-primary/20 group-hover:bg-primary/30 transition-colors">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Real-Time</h3>
                  <p className="text-xs text-muted-foreground">Performance</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 rounded-xl bg-card/30 backdrop-blur-sm border border-border/30 hover:bg-card/50 transition-all duration-300 group">
                <div className="p-2.5 rounded-lg bg-primary/20 group-hover:bg-primary/30 transition-colors">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Bank-Grade</h3>
                  <p className="text-xs text-muted-foreground">Security</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 rounded-xl bg-card/30 backdrop-blur-sm border border-border/30 hover:bg-card/50 transition-all duration-300 group">
                <div className="p-2.5 rounded-lg bg-primary/20 group-hover:bg-primary/30 transition-colors">
                  <Zap className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Lightning Fast</h3>
                  <p className="text-xs text-muted-foreground">Processing</p>
                </div>
              </div>
            </div>

            {/* AI Badge */}
            <div className="flex items-center gap-2 pt-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm">
                <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                <span className="text-sm font-medium text-primary">Enhanced with AI Technology</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="flex flex-col items-center justify-center p-6 sm:p-8 lg:p-12 bg-background relative">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-[0.02] bg-[radial-gradient(circle_at_1px_1px,_var(--primary)_1px,_transparent_0)] bg-[size:24px_24px]" />
          
          <div className={cn(
            "w-full max-w-md space-y-6 relative z-10",
            "animate-in fade-in-0 slide-in-from-right-8 duration-1000"
          )}>
            {/* Form Card */}
            <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
              {screen === "login" && <LoginForm setScreen={setScreen} />}
              {screen === "reset" && (
                <ResetPasswordForm 
                  onNext={(email) => {
                    setResetEmail(email);
                    setScreen("checkEmail");
                  }} 
                  onBack={() => setScreen("login")}
                />
              )}
              {screen === "checkEmail" && (
                <CheckEmail 
                  email={resetEmail}
                  onBack={() => setScreen("login")}
                />
              )}
              {screen === "setPassword" && (
                <SetNewPasswordForm 
                  onComplete={() => setScreen("login")} 
                  onBack={() => setScreen("login")}
                />
              )}
            </div>

            {/* Footer */}
            <div className="text-center text-sm text-muted-foreground">
              <p>© {new Date().getFullYear()} GrowFinX. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </PublicRoute>
  );
};

export default LoginPage;
